import { Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { DomSanitizer, type SafeUrl } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe } from "@ngx-translate/core";

import { PercentageValuePipe, TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import type { NationalAgendaSubGovModelType, VisionSubGovDirection } from "../../../models";
import { FilesApiService, LanguageService } from "../../../services";
import { CountUpComponent } from "../../count-up";

export type CommonMeasureType = {
  id?: number;
  logo: number;
  performance?: number;
  performanceColor?: number;
  visionAchievement?: number;
  visionAchievementColor?: number;
  ntKpiCount?: number;
  nameAE?: string;
  nameEN?: string;
};

@Component({
  selector: "adaa-uae-vision-perf-card",
  templateUrl: "./uae-vision-perf-card.html",
  styleUrl: "./uae-vision-perf-card.scss",
  imports: [TranslateTokenPipe, TranslatePipe, PercentageValuePipe, CountUpComponent],
})
export class UaeVisionPerfCardComponent {
  readonly router = inject(Router);
  readonly languageService = inject(LanguageService);
  private readonly _domSanitizer = inject(DomSanitizer);
  private readonly _fileService = inject(FilesApiService);

  data = input.required<VisionSubGovDirection | NationalAgendaSubGovModelType | CommonMeasureType>();
  logoType = input<"svg" | "normal">("normal");
  useIndicator = input<{
    performance: boolean;
    visionAchievement: boolean;
    ntkpiCount: boolean;
  }>({
    performance: true,
    visionAchievement: true,
    ntkpiCount: true,
  });

  cardClicked = output<CommonMeasureType>();

  logo = signal<SafeUrl | string | undefined>(undefined);

  annualColor = computed(() => this._evaluationColor(this.data().performance, this.data().performanceColor));
  overallColor = computed(() =>
    this._evaluationColor(this.data().visionAchievement, this.data().visionAchievementColor)
  );
  annualProgress = computed(() => this._progress(this.data().performance));
  overallProgress = computed(() => this._progress(this.data().visionAchievement));

  readonly defaultColor = Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_GRAY];
  readonly valueFontSize = "0.875rem";
  readonly viewIcon = {
    ltr: faAngleRight,
    rtl: faAngleLeft,
  };

  constructor() {
    effect(() => {
      this._downloadFile();
    });
  }

  public getColor(code: number) {
    return Constants.COLOR_CODES[code];
  }

  private _evaluationColor(value: number | undefined, colorCode: number | undefined) {
    if (!AdaaHelper.isDefined(value) || !AdaaHelper.isDefined(colorCode)) return this.defaultColor;
    return this.getColor(colorCode);
  }

  private _progress(value: number | undefined) {
    if (!AdaaHelper.isDefined(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  private _downloadFile() {
    this._fileService.download(this.data().logo).subscribe({
      next: async (blob) => {
        if (this.logoType() === "svg") {
          this._generateDataUrl(blob);
          return;
        }

        const dataUrl = window.URL.createObjectURL(blob);
        this.logo.update(() => dataUrl);
      },
    });
  }

  private _generateDataUrl = (blob: Blob) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const logo = atob(dataUrl?.split(",")[1]);

      this.logo.update(() => this._constructImageFromSvg(logo));
    };
  };

  private _constructImageFromSvg = (svg: string) => {
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    return this._domSanitizer.bypassSecurityTrustUrl(url);
  };
}
